import { Component, Host, h, Prop, State, Watch, Element, Method } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import { FormMessageSchemeDefinition, FormMessageSchemeName } from "./constants/form-message-types";
import {
  MESSAGE_ERROR,
  MESSAGE_SUCCESS,
  FORM_MESSAGE_SCHEME_CONFIG,
  FORM_MESSAGE_SCHEME_MAP,
  MESSAGE_INFO
} from "./constants/form-message-constants";
import { MessageType } from "./constants/form-message-types";
import pfx from "../../utils/style-class-prefix-helper";
import { FormMessageSchemeConfiguration } from "../../constants/colors";
import { ICONSIZE_SMALL } from "../icon/constants/icon-constants";
import { BackgroundColor } from "../background/constants/background-types";
import sanitizeHtml from "sanitize-html";
import { StyledComponent } from "../../decorators/styled-component";
// import { InitLogging } from "../../decorators/init-logging";

@Component({
  tag: "envc-eon-ui-form-message",
  styleUrl: "form-message.scss",
  shadow: true
})
export class FormMessage implements ColorableComponent<FormMessageSchemeConfiguration<FormMessageSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: FormMessageSchemeConfiguration<FormMessageSchemeDefinition> = FORM_MESSAGE_SCHEME_CONFIG;
  schemeMap = FORM_MESSAGE_SCHEME_MAP;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEnvcEonUiFormMessageElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or envc-eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: FormMessageSchemeName;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or envc-eon-ui-background component.
   */
  @State() activeScheme: FormMessageSchemeDefinition;

  /**
   * Icon name for internal use
   */
  @State() iconName: string;

  /**
   * Sets the message content
   * @example <envc-eon-ui-form-message message="this is a message"></envc-eon-ui-form-message>
   */
  @Prop() message: string;

  /**
   * Set the type of the message
   * @example <envc-eon-ui-form-message message="this is an error" type="error"></envc-eon-ui-form-message>
   */
  @Prop() type: MessageType;

  /**
   * Set the scheme of the icon
   * @example <envc-eon-ui-form-message icon-scheme-name="darkgrey"></envc-eon-ui-form-message>
   */
  @Prop() iconScheme: any; //needs to be any here so we can insert simple "string"

  /**
   * Name of the icon to be displayed in the message
   * @example <envc-eon-ui-form-message icon="alert"></envc-eon-ui-form-message>
   */
  @Prop({ mutable: true }) icon: string;

  componentDidLoad() {
    this.setCssVariables();
  }

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  componentWillLoad() {
    this.applyScheme();
    this.setIcon();
  }

  @Watch("type")
  setIcon() {
    if (!this.icon) {
      switch (this.type) {
        case MESSAGE_ERROR:
          this.iconName = "alert";
          break;
        case MESSAGE_SUCCESS:
          this.iconName = "radio_checkbox_tick";
          break;
        case MESSAGE_INFO:
          this.iconName = "";
          break;
      }
    } else {
      this.iconName = this.icon;
    }
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            ["form-message"]: true,
            ["info"]: this.type === MESSAGE_INFO,
            ["success"]: this.type === MESSAGE_SUCCESS,
            ["error"]: this.type === MESSAGE_ERROR
          })}
          role="alert"
        >
          {this.iconName && (
            <envc-eon-ui-icon
              aria-label="error"
              class={pfx("icon-content")}
              name={this.iconName}
              size={ICONSIZE_SMALL}
              noSpace={true}
            ></envc-eon-ui-icon>
          )}
          <span innerHTML={sanitizeHtml(this.message)} aria-live="assertive" aria-atomic="false"></span>
        </div>
      </Host>
    );
  }
}
